import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MailWarning, Loader2, RefreshCw, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export default function VerifyGate() {
  const { user, credits, resendVerification, refresh } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("melletic_verify_dismissed") === "1");

  const unverified = !!user && user.email_verified === false;
  // 0 (default) = lock immediately; >0 = grace period in minutes (admin configurable).
  const lockMinutes = Number(credits?.verify_lock_minutes ?? 0);

  useEffect(() => {
    if (!unverified) { localStorage.removeItem("melletic_verify_deadline"); setLocked(false); return; }
    if (!(lockMinutes > 0)) { localStorage.removeItem("melletic_verify_deadline"); setLocked(true); return; }
    const limitMs = lockMinutes * 60 * 1000;
    let dl = parseInt(localStorage.getItem("melletic_verify_deadline") || "0", 10);
    if (!dl || Number.isNaN(dl)) { dl = Date.now() + limitMs; localStorage.setItem("melletic_verify_deadline", String(dl)); }
    const check = () => setLocked(Date.now() >= dl);
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, [unverified, lockMinutes]);

  if (!unverified || !locked || dismissed) return null;

  const close = () => { sessionStorage.setItem("melletic_verify_dismissed", "1"); setDismissed(true); };

  const resend = async () => {
    setBusy(true);
    try {
      const data = await resendVerification();
      if (data?.dev_verify_url) {
        const url = data.dev_verify_url;
        if (url.startsWith(window.location.origin)) nav(url.slice(window.location.origin.length));
        else window.location.href = url;
      } else {
        toast.success(t("email_resent_toast"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("try_again_moment"));
    } finally { setBusy(false); }
  };

  const recheck = async () => {
    setBusy(true);
    try {
      const u = await refresh();
      if (u && u.email_verified === false) toast.error(t("verify_still"));
      else { localStorage.removeItem("melletic_verify_deadline"); toast.success(t("email_verified_toast") || "Verified!"); }
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" style={{ background: "rgba(11,23,39,0.55)", backdropFilter: "blur(8px)" }} data-testid="verify-gate">
      <div className="w-full max-w-[360px] bg-white rounded-3xl p-7 text-center shadow-2xl animate-pop relative">
        <button onClick={close} data-testid="verify-gate-close" aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
          <X size={18} />
        </button>
        <span className="mx-auto w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 mb-4"><MailWarning size={30} /></span>
        <h2 className="font-heading text-xl font-extrabold text-[#0B1727]" data-testid="verify-gate-title">{t("verify_lock_title")}</h2>
        <p className="text-slate-500 text-sm mt-2 leading-relaxed">{t("verify_lock_body")}</p>
        {user?.email && <p className="text-slate-800 font-semibold text-sm mt-3 break-all">{user.email}</p>}
        <button onClick={resend} disabled={busy} data-testid="verify-gate-resend"
          className="w-full mt-6 bg-blue-600 text-white font-bold py-3.5 rounded-2xl press disabled:opacity-60 flex items-center justify-center gap-2 shadow-[0_14px_34px_-14px_rgba(37,99,235,0.9)]">
          {busy ? <Loader2 className="animate-spin" size={17} /> : <MailWarning size={17} />} {t("resend_word")}
        </button>
        <button onClick={recheck} disabled={busy} data-testid="verify-gate-recheck"
          className="w-full mt-3 text-blue-600 font-semibold py-2.5 rounded-2xl press disabled:opacity-60 flex items-center justify-center gap-2">
          <RefreshCw size={16} /> {t("i_verified_btn")}
        </button>
      </div>
    </div>
  );
}
